import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Search, Sparkles, Pencil, Trash2 } from "lucide-react";
import { API, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Spinner, Empty } from "../components/common";
import { tempColor, scoreColor, stageBadge, stageLabel, typeBadge, timeAgo } from "../lib/helpers";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";

const SOURCES = ["Manual Entry", "Meta Lead Ads", "Instagram Lead Ads", "Website Form", "WhatsApp"];

export default function Leads() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const requestedType = new URLSearchParams(location.search).get("lead_type");
  const [leads, setLeads] = useState(null);
  const [type, setType] = useState(requestedType === "ASSOCIATE" ? "ASSOCIATE" : "INVESTOR");
  const [q, setQ] = useState("");
  const [tempFilter, setTempFilter] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", lead_type: "INVESTOR", source: "Manual Entry", budget: "", preferred_location: "", campaign: "" });
  const [saving, setSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");

  const load = useCallback(() => {
    setLeads(null);
    const params = new URLSearchParams({ lead_type: type });
    if (q) params.set("q", q);
    if (tempFilter) params.set("temperature", tempFilter);
    API.get(`/leads?${params}`).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [type, q, tempFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const nextType = new URLSearchParams(location.search).get("lead_type");
    if (nextType === "ASSOCIATE" || nextType === "INVESTOR") setType(nextType);
  }, [location.search]);

  const createLead = async () => {
    if (!form.name || !form.phone) { toast.error("Name and phone required"); return; }
    setSaving(true);
    try {
      const { data } = await API.post("/leads", { ...form, lead_type: type });
      if (data.duplicate) toast.warning("Duplicate detected — merged into existing lead.");
      else toast.success("Lead created & analyzed by AI");
      setOpenNew(false);
      setForm({ name: "", phone: "", email: "", lead_type: type, source: "Manual Entry", budget: "", preferred_location: "", campaign: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await API.post("/leads/import", fd);
      setImportResult(data);
      toast.success(`Imported ${data.imported}, ${data.duplicates} duplicates, ${data.failed} failed`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveEdit = async () => {
    if (!editing?.name?.trim() || !editing?.phone?.trim()) return toast.error("Name and phone are required");
    setSaving(true);
    try {
      await API.put(`/leads/${editing.id}`, editing);
      toast.success("Lead updated"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const deleteLead = async () => {
    if (deleteConfirm !== deleting?.name) return toast.error("Type the lead name exactly");
    setSaving(true);
    try {
      await API.delete(`/leads/${deleting.id}`, { params: { confirm: deleteConfirm } });
      toast.success("Lead and its linked activity were deleted"); setDeleting(null); setDeleteConfirm(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Leads" subtitle="Complete lead database with AI scoring." testid="leads-header">
        <Dialog open={openImport} onOpenChange={setOpenImport}>
          <DialogTrigger asChild>
            <button className="btn-secondary bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-medium px-4 py-2 rounded-lg flex items-center gap-2 text-sm" data-testid="import-csv-btn">
              <Upload className="h-4 w-4" /> Import CSV
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
            <DialogHeader><DialogTitle>Import Leads (CSV)</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-400">Columns supported: name, phone, email, lead_type, campaign, budget, location, project.</p>
            <input type="file" accept=".csv" onChange={doImport} data-testid="csv-file-input"
              className="text-sm text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-amber-600 file:text-slate-950 file:font-semibold" />
            {importResult && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="import-summary">
                {[["Imported", importResult.imported], ["Updated", importResult.updated], ["Duplicates", importResult.duplicates], ["Failed", importResult.failed]].map(([k, v]) => (
                  <div key={k} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
                    <div className="font-heading text-xl font-bold text-slate-100">{v}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{k}</div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <button className="btn-gold px-4 py-2 flex items-center gap-2 text-sm" data-testid="new-lead-btn">
              <Plus className="h-4 w-4" /> New Lead
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-lg">
            <DialogHeader><DialogTitle>Create {type === "ASSOCIATE" ? "Associate" : "Investor"} Lead</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[["name", "Full Name *"], ["phone", "Phone *"], ["email", "Email"], ["campaign", "Campaign"], ["budget", "Budget"], ["preferred_location", "Preferred Location"]].map(([k, label]) => (
                <div key={k} className={k === "name" || k === "phone" ? "" : ""}>
                  <label className="text-xs text-slate-400 mb-1 block">{label}</label>
                  <input data-testid={`lead-field-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-slate-400 mb-1 block">Source</label>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200">
                  {SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter>
              <button onClick={createLead} disabled={saving} className="btn-gold px-4 py-2 text-sm" data-testid="save-lead-btn">
                {saving ? "Saving…" : "Create & Analyze"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[#111827] border-slate-800 text-slate-100 max-w-xl">
          <DialogHeader><DialogTitle>Edit lead</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["name","Full name"],["phone","Phone"],["whatsapp","WhatsApp"],["email","Email"],["campaign","Campaign"],["budget","Budget"],["preferred_location","Preferred location"],["project_interest","Project interest"]].map(([k,label])=><label key={k} className="text-xs text-slate-400">{label}<input value={editing[k]||""} onChange={e=>setEditing({...editing,[k]:e.target.value})} className="mt-1 w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40"/></label>)}
            <label className="text-xs text-slate-400">Stage<select value={editing.stage||"NEW"} onChange={e=>setEditing({...editing,stage:e.target.value})} className="mt-1 w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="NEW">New</option><option value="CONTACTED">Contacted</option><option value="QUALIFIED">Qualified</option><option value="SITE_VISIT">Site visit</option><option value="NEGOTIATION">Negotiation</option><option value="WON">Won</option><option value="LOST">Lost</option></select></label>
            <label className="text-xs text-slate-400">Lead type<select value={editing.lead_type||"INVESTOR"} onChange={e=>setEditing({...editing,lead_type:e.target.value})} className="mt-1 w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm"><option value="INVESTOR">Investor</option><option value="ASSOCIATE">Associate</option></select></label>
          </div>}
          <DialogFooter><button className="btn-gold px-4 py-2 text-sm" disabled={saving} onClick={saveEdit}>{saving?"Saving…":"Save changes"}</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => { if(!open){setDeleting(null);setDeleteConfirm("");} }}>
        <DialogContent className="bg-[#111827] border-red-900/60 text-slate-100 max-w-md">
          <DialogHeader><DialogTitle>Delete lead permanently?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-400">This also removes linked messages, notes, events and follow-ups. Type <b className="text-slate-100">{deleting?.name}</b> to confirm.</p>
          <input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)} className="w-full bg-slate-900 border border-red-900/60 rounded-lg px-3 py-2" placeholder="Exact lead name"/>
          <DialogFooter><button className="rounded-lg bg-red-600 hover:bg-red-500 px-4 py-2 text-sm font-semibold disabled:opacity-40" disabled={saving||deleteConfirm!==deleting?.name} onClick={deleteLead}>{saving?"Deleting…":"Delete permanently"}</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900/50 p-1">
          <button data-testid="lead-type-toggle-investor" onClick={() => setType("INVESTOR")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${type === "INVESTOR" ? "bg-blue-500/20 text-blue-300" : "text-slate-400"}`}>Investors</button>
          <button data-testid="lead-type-toggle-associate" onClick={() => setType("ASSOCIATE")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${type === "ASSOCIATE" ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400"}`}>Associates</button>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone…"
            className="w-full bg-slate-900/70 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500/40" />
        </div>
        <select data-testid="temp-filter" value={tempFilter} onChange={(e) => setTempFilter(e.target.value)}
          className="bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-300">
          <option value="">All temperatures</option>
          <option value="hot">🔥 Hot</option><option value="qualified">Qualified</option>
          <option value="warm">Warm</option><option value="cold">Cold</option>
        </select>
      </div>

      {!leads ? <Spinner /> : leads.length === 0 ? <Empty label="No leads match your filters." /> : (
        <div className="mb-card overflow-hidden" data-testid="leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-800">
                  <th className="px-4 py-3 font-medium">Lead</th>
                  <th className="px-4 py-3 font-medium">Score</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Stage</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Source</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Budget</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Updated</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}
                    className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold ${l.lead_type === "ASSOCIATE" ? "bg-emerald-500/15 text-emerald-400" : "bg-blue-500/15 text-blue-400"}`}>
                          {l.name.charAt(0)}
                        </div>
                        <div>
                          <div className="text-slate-200 font-medium flex items-center gap-2">{l.name}
                            {l.needs_human && <Sparkles className="h-3 w-3 text-purple-400" title="Needs human" />}
                          </div>
                          <div className="text-xs text-slate-500">{l.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={`font-heading font-bold ${scoreColor(l.score)}`}>{l.score}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${tempColor(l.temperature)}`}>{l.temperature}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${stageBadge(l.stage)}`}>{stageLabel(l.stage).toLowerCase()}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs">{l.source}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-400 text-xs">{l.budget || "—"}</td>
                    <td className="px-4 py-3 hidden sm:table-cell text-slate-500 text-xs">{timeAgo(l.updated_at)}</td>
                    <td className="px-4 py-3"><div className="flex justify-end gap-1">
                      <button aria-label={`Edit ${l.name}`} title="Edit lead" onClick={e=>{e.stopPropagation();setEditing({...l})}} className="rounded-lg p-2 text-slate-400 hover:bg-blue-500/15 hover:text-blue-300 transition"><Pencil className="h-4 w-4"/></button>
                      {isAdmin&&<button aria-label={`Delete ${l.name}`} title="Delete lead" onClick={e=>{e.stopPropagation();setDeleting(l);setDeleteConfirm("")}} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/15 hover:text-red-300 transition"><Trash2 className="h-4 w-4"/></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
